import React, { useCallback } from 'react';
import { Input } from 'antd';
import { Flex } from 'antd-v5';
import {
  PlusOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';

import ScheduleDatePicker from './ScheduleDatePicker';
import WhoseScheduledDrawer from './WhoseScheduledDrawer';
import OnTraccrButton from '../common/buttons/OnTraccrButton';

import Permissions from '../auth/Permissions';
import { getViewDateRange } from './scheduleHelpers';
import ScheduleFilterDropdown from './Filters/ScheduleFilterDropdown';
import ScheduleLayoutToggle from './ScheduleLayoutToggle';
import TimecardHeaderButton from '../timecards/TimecardHeaderButton';
import { ReactComponent as DownloadIcon } from '../assets/icons/download.svg';

const GUTTER = 4;

function ScheduleHeader({
  viewType,
  onViewTypeChange,
  onDateChange,
  date,
  onSearch,
  onAdd,
  onExport,
  enableHotkeys,
  disableHotkeys,
  openFilterDrawer,
  filters,
  filterViews,
  isSideBarOpen,
  layout,
  onLayoutChange,
}) {
  const onSearchChange = useCallback((e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    onSearch(value);
  }, [onSearch]);

  const onInputVisibleChanged = useCallback((visible) => {
    if (visible) {
      disableHotkeys();
    } else {
      enableHotkeys();
    }
  }, [enableHotkeys, disableHotkeys]);

  const { startDate, endDate } = getViewDateRange(date, viewType);

  const typeMap = {
    Day: 'date',
    Week: 'week',
    Month: 'month',
    UserDay: 'date',
    UserBiWeekly: 'week',
    UserWeekdays: 'week',
    UserWeek: 'week',
    EquipmentDay: 'date',
    EquipmentBiWeekly: 'week',
    EquipmentWeekdays: 'week',
    EquipmentWeek: 'week',
  };

  return (
    <Flex
      id="schedule-header-container"
      justify="space-between"
      align="center"
      wrap
    >
      <Flex align="center" wrap gap={GUTTER}>
        <ScheduleFilterDropdown
          filters={filters}
          filterViews={filterViews}
          onEdit={openFilterDrawer}
          onSearchFocus={disableHotkeys}
          onSearchBlur={enableHotkeys}
        />
        <Input.Search
          className="schedule-header-searchbar searchbar"
          placeholder="Search shifts"
          onChange={onSearchChange}
          allowClear
          onFocus={disableHotkeys}
          onBlur={enableHotkeys}
          style={{
            width: 250,
          }}
        />
        <WhoseScheduledDrawer startDate={startDate} endDate={endDate} />
      </Flex>
      <Flex align="center" wrap gap={GUTTER}>
        <span style={{ display: 'inline-flex' }}>
          <TimecardHeaderButton
            ariaLabel="Export"
            onClick={onExport}
            className="schedule-header-export-button"
          >
            <DownloadIcon />
          </TimecardHeaderButton>
        </span>
        <ScheduleLayoutToggle value={layout} onChange={onLayoutChange} />
        <ScheduleDatePicker
          viewType={viewType}
          onViewTypeChange={onViewTypeChange}
          onDateChange={onDateChange}
          date={date}
          onVisibleChanged={onInputVisibleChanged}
          typeMap={typeMap}
        />
        {Permissions.has('SCHEDULE_WRITE') && (
          <OnTraccrButton
            title="Add"
            icon={<PlusOutlined />}
            onClick={onAdd}
          />
        )}
      </Flex>
    </Flex>
  );
}

/* eslint-disable react/forbid-prop-types */
ScheduleHeader.propTypes = {
  viewType: PropTypes.string.isRequired,
  onViewTypeChange: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  date: PropTypes.instanceOf(DateTime).isRequired,
  onSearch: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  enableHotkeys: PropTypes.func.isRequired,
  disableHotkeys: PropTypes.func.isRequired,
  openFilterDrawer: PropTypes.func.isRequired,
  filters: PropTypes.shape({}),
  filterViews: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    filterViewName: PropTypes.string,
  })),
  isSideBarOpen: PropTypes.bool.isRequired,
  layout: PropTypes.oneOf(['calendar', 'list']),
  onLayoutChange: PropTypes.func,
};

ScheduleHeader.defaultProps = {
  filters: {},
  filterViews: [],
  layout: 'calendar',
  onLayoutChange: undefined,
};

export default ScheduleHeader;
